// Hostnames without a real TLD that are used only in development. For these
// the whole single label is treated as the registered domain, so
// `www.localhost` and `localhost` count as the same site.
const DEV_TLDS = ['localhost', 'test']

function registeredDomain(host) {
  const parts = host.split('.')
  if (parts.length <= 1) return host
  const tld = parts[parts.length - 1]
  return DEV_TLDS.includes(tld) ? tld : parts.slice(-2).join('.')
}

function isExternalHost(sourceHost, targetHost) {
  if (!sourceHost || !targetHost || sourceHost === targetHost) return false
  return registeredDomain(sourceHost) !== registeredDomain(targetHost)
}

function explodeUrl(url, rewriteMap) {
  const { protocol, host, rest } = url.match(/^(?<protocol>[a-z][a-z0-9+\-.]*:)?(\/\/(?<host>[^/]+))?(?<rest>.*$)/i).groups
  const replacedHost = rewriteMap[host] || host || ''
  const type = [undefined, 'http:', 'https:'].includes(protocol) ? 'web' : 'other'
  return { url: [type === 'web' ? (replacedHost ? '//' : '') : protocol, replacedHost, rest].join(''), host: replacedHost, protocol, rest, type }
}

/**
 * Provides information on a link.
 *
 * Returns:
 *  - scope:      'inside' when the link is path-only or on the exact same host; 'outside' otherwise.
 *  - isExternal: true when the link goes to a different registered domain (e.g. example.com → frayja.com).
 *                Subdomains of the same registered domain (www.example.com ↔ test.example.com) are NOT external.
 *  - type:       'web' for http(s), 'other' for mailto:/tel:/etc.
 *  - url:        a path when inside, otherwise the absolute url after rewrites.
 *
 * Example:
 * > linkDetails('//example.com/foo', '//www.example.com/bar', { rewrites:[['www.example.com', 'example.com']] })
 * > --> { isExternal: false, scope: 'inside', type: 'web', url: '/bar' }
 */
export function linkDetails(sourceUrl, targetUrl, options) {
  // Convert lists of rewrites into a map
  // [['www.example.com', 'example.com']] -> {'www.example.com': 'www.example.com', 'example.com': 'www.example.com'}
  const rewriteMap = Object.fromEntries((options?.rewrites || []).flatMap((rewrites) => rewrites.map((rewrite) => [rewrite, rewrites[0]])))

  // Explode the url into its parts, and perform possible rewrites
  const source = explodeUrl(sourceUrl || '', rewriteMap)
  const target = explodeUrl(targetUrl || '', rewriteMap)

  // The link `type` determines whether it links to a web page or something else, i.e. an email link
  const type = target.type

  // The link `scope` determines whether the link stays inside the app (path-only or same host)
  // or goes outside (any other host). This drives whether we emit a path or an absolute url.
  const scope = type === 'web' && (source.host === '' || ['', source.host].includes(target.host)) ? 'inside' : 'outside'

  // A link is `external` only when it leaves the registered domain — i.e. subdomain hops
  // (jobs.example.com ↔ www.example.com) stay internal, but example.com ↔ frayja.com is external.
  const isExternal = scope === 'outside' && isExternalHost(source.host, target.host)

  // The link `url` is a relative url when `inside` the app or otherwise an absolute url.
  // A bare same-host URL like `https://example.com` yields an empty `rest`; normalize that
  // to `/` so the homepage is still a valid href.
  const url = scope === 'inside' ? (target.rest === '' && target.host !== '' ? '/' : target.rest) : target.url

  return { isExternal, scope, type, url }
}
